/**
 * Inline references inside an Action's brief.
 *
 * The brief is the single source of truth for what an Action reads: tagging a
 * bin or asset in the prose *is* the connection, so the inspector's editor,
 * the canvas card, and the derived edges all round-trip through this format.
 */

import type { ActionInputRef } from '@/types/shared'

export type WorkflowTagKind = 'asset' | 'bin' | 'model' | 'variable' | 'note' | 'instructions'

export interface WorkflowTag {
  kind: WorkflowTagKind
  id: string
  label: string
}

/**
 * `@[Kind:Label](id)` — Kind is capitalized, label may contain parentheses.
 * An Instructions tag's id is a project-file path (segments `[A-Za-z0-9._-]`).
 */
export const WORKFLOW_TAG_PATTERN = /@\[(Asset|Bin|Model|Variable|Note|Instructions):([^\]]+)\]\(([^)]+)\)/g

/** Labels are embedded in a single-line token, so newlines and `]` can't survive. */
export function cleanWorkflowTagLabel(label: string): string {
  return label
    .replace(/[\]\n\r]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

export function serializeWorkflowTag(tag: WorkflowTag): string {
  const type = `${tag.kind[0].toUpperCase()}${tag.kind.slice(1)}`
  return `@[${type}:${cleanWorkflowTagLabel(tag.label) || tag.id}](${tag.id})`
}

export function collectWorkflowTags(value: string): ActionInputRef[] {
  const refs: ActionInputRef[] = []
  const seen = new Set<string>()
  for (const match of value.matchAll(new RegExp(WORKFLOW_TAG_PATTERN.source, 'g'))) {
    const type = match[1].toLowerCase() as WorkflowTagKind
    const id = match[3]
    const key = `${type}:${id}`
    if (seen.has(key)) continue
    seen.add(key)
    refs.push({ type, id, label: match[2] })
  }
  return refs
}

/**
 * Replace every tag of `kind` whose target was deleted with plain text, so the
 * brief reads sensibly ("[missing bin: Selects]") instead of pointing at a
 * ghost id. Mirrors the backend's `stripWorkflowTags` (instantiate path).
 */
export function stripWorkflowTags(instruction: string, kind: WorkflowTagKind, deletedIds: ReadonlySet<string>): string {
  const wanted = `${kind[0].toUpperCase()}${kind.slice(1)}`
  return instruction.replace(
    new RegExp(WORKFLOW_TAG_PATTERN.source, 'g'),
    (whole, tagKind: string, label: string, id: string) =>
      tagKind === wanted && deletedIds.has(id) ? `[missing ${kind}: ${label}]` : whole,
  )
}

/** Append a tag to a brief, skipping ids already referenced. */
export function appendWorkflowTag(instruction: string, tag: WorkflowTag): string {
  if (collectWorkflowTags(instruction).some(ref => ref.type === tag.kind && ref.id === tag.id)) return instruction
  const trimmed = instruction.trimEnd()
  return trimmed ? `${trimmed} ${serializeWorkflowTag(tag)}` : serializeWorkflowTag(tag)
}
